import { ModButton, ModIconButton } from '@moderneinc/design-system-components';
import {
  borderRadius,
  iconSize,
  semanticColors,
  typography
} from '@moderneinc/design-system-tokens';
import { Card, Stack, Typography } from '@mui/material';
import type { FunctionComponent, ReactNode } from 'react';
import type { TooltipRenderProps } from 'react-joyride';
import { ChevronLeftIcon, CloseIcon } from '../../icons/icons';
import { getEngineStepData } from './tour-engine.types';

const CARD_SX = {
  p: 2.5,
  minWidth: 280,
  borderRadius: `${borderRadius.s}px`,
  boxShadow: (theme: { shadows: string[] }) => theme.shadows[8]
} as const;

/**
 * Renders a content string with **bold** markers as <strong> and \n\n as
 * separate paragraphs. Passes through non-string ReactNode content unchanged.
 */
export const renderRichContent = (content: ReactNode) => {
  if (typeof content !== 'string') {
    return content;
  }
  return content.split('\n\n').map((para, p) => (
    <Typography
      key={p}
      variant="bodySm"
      sx={{
        color: semanticColors.color.text.primary,
        mt: p === 0 ? 0 : 1.5
      }}
    >
      {para
        .split(/(\*\*[^*]+\*\*)/g)
        .map((part, i) =>
          part.startsWith('**') && part.endsWith('**') ? (
            <strong key={i}>{part.slice(2, -2)}</strong>
          ) : (
            part
          )
        )}
    </Typography>
  ));
};

/**
 * MUI-styled tooltip for React Joyride tours.
 *
 * The Day Zero engine tour carries phase data (phaseNumber/phaseTotal/phaseLabel)
 * and renders a phase label + progress dots; it uses "Skip tour" rather than a
 * close button and shows Back only on steps that declare a safe re-entry point
 * (`data.backTo`) — steps whose Back would land inside an open, focus-trapping
 * dialog omit it. Legacy per-page tours (changelog, builder, devcenter) carry no
 * phase data and keep the classic affordances: a close (X) button, an index/size
 * step counter, and Back.
 */
export const TourTooltip: FunctionComponent<TooltipRenderProps> = ({
  continuous,
  index,
  size,
  step,
  isLastStep,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps
}) => {
  const data = getEngineStepData(step);
  const phaseTotal = data?.phaseTotal;
  const phaseNumber = data?.phaseNumber;
  const phaseLabel = data?.phaseLabel;

  const hasPhaseData = phaseTotal !== undefined && phaseNumber !== undefined;

  // Legacy per-page tours: classic close / counter / back layout.
  if (!hasPhaseData) {
    return (
      <Card {...tooltipProps} sx={{ ...CARD_SX, maxWidth: 380 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1
          }}
        >
          {step.title && (
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: typography.fontWeight.semiBold }}
            >
              {step.title}
            </Typography>
          )}
          <ModIconButton
            {...closeProps}
            variant="tertiary"
            size="small"
            sx={{ ml: 'auto', mt: -0.5, mr: -1 }}
            aria-label="Close tour"
          >
            <CloseIcon size={iconSize.md} />
          </ModIconButton>
        </Stack>

        <Stack direction="column" sx={{ mt: step.title ? 1 : 0 }}>
          {renderRichContent(step.content)}
        </Stack>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2.5
          }}
        >
          <ModButton variant="tertiary" size="small" {...skipProps}>
            Skip tour
          </ModButton>

          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', mr: 1 }}
            >
              {index + 1} / {size}
            </Typography>

            {index > 0 && (
              <ModButton variant="secondary" size="small" {...backProps}>
                Back
              </ModButton>
            )}

            {continuous && (
              <ModButton variant="primary" size="small" {...primaryProps}>
                {isLastStep ? 'Done' : 'Next'}
              </ModButton>
            )}
          </Stack>
        </Stack>
      </Card>
    );
  }

  // Side-placed steps (e.g. a tooltip pinned to the right of a centered modal)
  // have limited horizontal room, so cap the width tighter than the default to
  // keep the tooltip from overlapping the element it points at.
  const isSidePlaced =
    typeof step.placement === 'string' &&
    (step.placement.startsWith('left') || step.placement.startsWith('right'));

  // Day Zero engine tour: phase label + progress dots.
  return (
    <Card
      {...tooltipProps}
      sx={{ ...CARD_SX, maxWidth: isSidePlaced ? 320 : 400 }}
    >
      <Stack direction="column" sx={{ gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'primary.main',
            fontWeight: typography.fontWeight.semiBold,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            lineHeight: 1.4
          }}
        >
          {`Step ${phaseNumber} of ${phaseTotal}: ${phaseLabel ?? ''}`.toUpperCase()}
        </Typography>

        {step.title && (
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: typography.fontWeight.bold }}
          >
            {step.title}
          </Typography>
        )}

        <Stack direction="column" sx={{ mt: 0.5 }}>
          {renderRichContent(step.content)}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2.5
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
          {data?.backTo && (
            <ModButton
              variant="tertiary"
              size="small"
              startIcon={<ChevronLeftIcon size={iconSize.md} />}
              sx={{
                color: 'text.secondary',
                fontWeight: typography.fontWeight.regular
              }}
              {...backProps}
            >
              Back
            </ModButton>
          )}

          <ModButton
            variant="tertiary"
            size="small"
            sx={{
              color: 'text.secondary',
              fontWeight: typography.fontWeight.regular
            }}
            {...skipProps}
          >
            Skip tour
          </ModButton>
        </Stack>

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1
          }}
        >
          <Stack
            direction="row"
            sx={{ alignItems: 'center', gap: 0.75 }}
            aria-hidden="true"
          >
            {Array.from({ length: phaseTotal }, (_, i) => {
              const dotNumber = i + 1;
              const isActive = dotNumber === phaseNumber;
              return (
                <Stack
                  key={dotNumber}
                  sx={{
                    width: isActive ? '22px' : '6px',
                    height: '6px',
                    borderRadius: '999px',
                    backgroundColor: isActive
                      ? 'primary.main'
                      : 'action.disabledBackground',
                    transition: 'width 0.2s ease'
                  }}
                />
              );
            })}
          </Stack>

          {continuous && data?.advance !== 'action' && (
            <ModButton variant="primary" size="small" {...primaryProps}>
              {isLastStep ? 'Done' : 'Next'}
            </ModButton>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
